import { z } from "zod";

import { apiDelete, apiGet, apiPost, apiPut } from "../lib/api";

// ─── Sending profile records ──────────────────────────────────────────────────
//
// Per-customer SMTP sender identity for the Email channel. Mirrors enforcer
// SendingProfile, MINUS the credentials: the SMTP username/password live in
// Secrets Manager (smtp_credentials_secret_arn server-side) and are NEVER
// returned by any read. The UI only learns whether creds are set, via
// has_credentials.

export const SendingProfileSchema = z.object({
  sending_profile_id: z.string(),
  customer_id: z.string(),
  name: z.string(),
  from_address: z.string(),
  from_display_name: z.string(),
  smtp_host: z.string(),
  smtp_port: z.number().int(),
  smtp_use_tls: z.boolean().default(true),
  // Server-derived: true once a secret has been written for this profile.
  // The secret value / ARN itself is never sent to the client.
  has_credentials: z.boolean().default(false),
  created_at: z.string(),
});
export type SendingProfile = z.infer<typeof SendingProfileSchema>;

export const SendingProfileListResponseSchema = z.object({
  items: z.array(SendingProfileSchema),
  total: z.number(),
});
export type SendingProfileListResponse = z.infer<typeof SendingProfileListResponseSchema>;

// create/get/update return the record wrapped: { record: <profile> }.
export const SendingProfileDetailResponseSchema = z.object({
  record: SendingProfileSchema,
});

// ─── Write bodies ──────────────────────────────────────────────────────────────

export interface SendingProfileCreateBody {
  customer_id: string;
  name: string;
  from_address: string;
  from_display_name: string;
  smtp_host: string;
  smtp_port: number;
  smtp_use_tls: boolean;
}

export interface SendingProfileUpdateBody {
  name?: string;
  from_address?: string;
  from_display_name?: string;
  smtp_host?: string;
  smtp_port?: number;
  smtp_use_tls?: boolean;
}

// Write-only: the UI POSTs username/password; the server writes them to Secrets
// Manager and returns the (credential-free) profile. Never read back.
export interface SendingProfileCredentialsBody {
  username: string;
  password: string;
}

// ─── Single-tenant customer scope ────────────────────────────────────────────────
// Matches Groups/List.tsx CUSTOMER_ID and Engagements/List.tsx DEFAULT_CUSTOMER_ID.
const CUSTOMER_ID = "00000000-0000-0000-0000-000000000001";

// ─── Fetchers ────────────────────────────────────────────────────────────────────

export function fetchSendingProfiles() {
  return apiGet(
    `/api/sending-profiles?customer_id=${CUSTOMER_ID}`,
    SendingProfileListResponseSchema,
  );
}

export async function fetchSendingProfile(id: string) {
  const { record } = await apiGet(
    `/api/sending-profiles/${id}`,
    SendingProfileDetailResponseSchema,
  );
  return record;
}

export async function createSendingProfile(body: SendingProfileCreateBody) {
  const { record } = await apiPost(
    "/api/sending-profiles",
    SendingProfileDetailResponseSchema,
    body,
  );
  return record;
}

export async function updateSendingProfile(id: string, body: SendingProfileUpdateBody) {
  const { record } = await apiPut(
    `/api/sending-profiles/${id}`,
    SendingProfileDetailResponseSchema,
    body,
  );
  return record;
}

export function deleteSendingProfile(id: string) {
  return apiDelete(`/api/sending-profiles/${id}`);
}

// The server returns 204 No Content (sending_profiles.py:102,127) — it does NOT
// return the refreshed profile, despite what this comment used to claim.
//
// The old code did `const { record } = await apiPost(...)`. lib/api.ts's shared
// request helper returns `undefined` on 204, so that destructure threw a
// TypeError: the SMTP credential WAS written, the mutation's onError fired, the
// operator saw "Failed to save", and the has_credentials badge never flipped —
// on a write that had actually succeeded.
//
// Returns void. useSetSendingProfileCredentials already invalidates the
// ["sending-profiles"] query on success, which is what flips the badge; that
// onSuccess was simply never reached.
export async function setSendingProfileCredentials(
  id: string,
  body: SendingProfileCredentialsBody,
): Promise<void> {
  await apiPost(
    `/api/sending-profiles/${id}/credentials`,
    z.void(),
    body,
  );
}
